/* eslint-disable no-prototype-builtins */

import * as React from 'react';
import NextHead from 'next/head';
// Constants.
import { ROOT_TOKEN } from '../../constants/AppConstants';
// Utilities.
import { removeProtocolOrSlash } from '../../utils/Text';
// Components.
import HeadProps, {
  HeadData,
  LinkAttr,
  LinkData,
  LinkTags,
  MetaAttr,
  MetaData,
  ScriptAttr,
  ScriptData,
  ScriptTags,
  SimpleJson
} from './HeadProps';
import HeadState from './HeadState';
import { fireErrorPageViewEvent } from '@directv-digital/dds.services.analytics-service';

/**
 * NOTE: keys should be generated in the following convention:
 * {tagName}-{{relValueIfAvailable}-}{srcWithProtocolRemovedOrFronSlasheRemoved}
 */
export default class Head extends React.PureComponent<HeadProps, HeadState> {
  constructor(props: HeadProps) {
    super(props);
    const headData: any = props.data?.elements ?
      props.data.elements :
      props.data;
    this.state = this.buildStateObject(
      headData || {},
      props.page || "",
      props.flag
    );
    // console.log("props.data", props.data)
  }

  // This should be looked at and potentially updated to getDerivedStateFromProps in future versions.
  // UNSAFE_componentWillReceiveProps will go away in React version 17!!!

  UNSAFE_componentWillReceiveProps(nextProps: HeadProps) {
    const rawNewData = nextProps.data || {};
    const newData: any = rawNewData?.elements ? rawNewData.elements : rawNewData;
    const newPage = nextProps.page || "";
    if (
      JSON.stringify(rawNewData) !== JSON.stringify(this.props.data || {}) ||
      newPage !== this.props.page
    ) {
      this.setState(this.buildStateObject(newData, newPage, nextProps.flag));
    }
  }

  render(): JSX.Element {
    const { state } = this;
    const preloadRobotoFonts = (
      <>
        <link
          as="font"
          crossOrigin="anonymous"
          href="/dtvassets/global/fonts/Roboto-Bold.woff2"
          rel="preload"
          type="font/woff2"
        />
        <link
          as="font"
          crossOrigin="anonymous"
          href="/dtvassets/global/fonts/Roboto-Regular.woff2"
          rel="preload"
          type="font/woff2"
        />
        <link
          as="font"
          crossOrigin="anonymous"
          href="/dtvassets/global/fonts/Roboto-Medium.woff2"
          rel="preload"
          type="font/woff2"
        />
      </>
    );

    return this.props.data || this.props.default ? (
      <>
        {this.props.flag === 'head' && <NextHead>
          {/* Meta tags MUST be first. DO NOT add anything above these. */}
          {state?.meta?.[0]}
          {state?.meta?.[2]}
          {/* Title MUST come after meta tags. DO NOT add anything above this. */}
          {state.title}
          {state?.link?.[0]}
          <link
            as="font"
            crossOrigin="anonymous"
            href="/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Medium.woff2"
            rel="preload"
            type="font/woff2"
          />
          <link
            as="font"
            crossOrigin="anonymous"
            href="/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Regular.woff2"
            rel="preload"
            type="font/woff2"
          />
          <link
            as="font"
            crossOrigin="anonymous"
            href="/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Bold.woff2"
            rel="preload"
            type="font/woff2"
          />
          <link
            as="font"
            crossOrigin="anonymous"
            href="/dtvassets/sales/uf/dtv-ui/fonts/PFDINTextPro-Light.woff2"
            rel="preload"
            type="font/woff2"
          />
          {/* <link
            type="font/woff2"
            crossOrigin="anonymous"
            href="/dtvassets/sales/uf/dtv-ui/fonts/Jubilat-BoldItalic.woff2"
            rel="preload"
            as="font"
          /> */}
          {preloadRobotoFonts}
          {state.style}
          {state?.link}
          {state?.meta?.[1]}
          {state?.meta?.slice(3) || state?.meta}
          {this.props.children}
          {state.script}
        </NextHead>}
        {this.props.flag === 'body' &&
          <>
            {/** Note: Passing link and script tag to the body tag, it comes at the top */}
            {state.bodyLink}
            {state.bodyScript}
            {state.bodyStyle}
          </>
        }
      </>
    ) : null;
  }

  private errorEventFired = false;
  private buildStateObject = (
    data: HeadData,
    page: string,
    flag?: any
  ): HeadState => {
    const { globalOverrides } = this.props;
    const globalScripts = [];
    const globalStyles = [];

    if (globalOverrides && globalOverrides.length) {
      globalOverrides.forEach(override => {
        if (override.type === "application/javascript") {
          globalScripts.push(override);
        }
        if (override.type === "style") {
          globalStyles.push(override.value);
        }
      });
    }

    const isBrowser = typeof window !== 'undefined';
    if (page === "404" && isBrowser && !this.errorEventFired) {
      this.errorEventFired = true;
      setTimeout(() => {
        const event = {
          event: "error_page_view",
          error_type: "404",
          url: window.location.href,
          timestamp: new Date().toISOString()
        };
        fireErrorPageViewEvent(event);
      }, 0);
    }

    // Each <Head> instance renders only its half (head vs body). Compute only
    // the fields the rendering branch will read so the other half isn't built.
    if (flag === 'head') {
      return {
        link: this.getLinkTags(data.link),
        meta: this.getMetaTags(data.meta),
        script: this.getScriptTags(data.script, globalScripts),
        style: this.getStyles(data.styles, globalStyles),
        title: this.getTitleTag(data.title)
      };
    }
    if (flag === 'body') {
      return {
        bodyLink: this.getLinkTags(data?.bodyPageProperties && data?.bodyPageProperties?.length > 0 ? data?.bodyPageProperties[0]?.link : data?.bodyPageProperties?.link),
        bodyScript: this.getScriptTags(data?.bodyPageProperties && data?.bodyPageProperties?.length > 0 ? data?.bodyPageProperties[0]?.script : data?.bodyPageProperties?.script),
        bodyStyle: this.getStyles(data?.bodyPageProperties && data?.bodyPageProperties?.length > 0 ? data?.bodyPageProperties[0]?.styles : data?.bodyPageProperties?.styles, globalStyles)
      };
    }
    return {};
  };

  private getLinks = (
    link: any[],
    rel: string,
    asValue?: string
  ): JSX.Element[] => {
    const linkTags: any[] = link && Array.isArray(link) ? link : [];
    return linkTags.map((linkTag: any, index: number): JSX.Element => {
      if (linkTag && typeof linkTag === "string") {
        const props: LinkData = {
          [LinkAttr.href]: linkTag.replace(ROOT_TOKEN, this.props.root),
          [LinkAttr.rel]: rel
        };
        // if (hreflang) { props[LinkAttr.hreflang] = hreflang; }
        if (asValue) {
          props[LinkAttr.as] = asValue;
        }
        const key = `${rel}${asValue || ``}-${removeProtocolOrSlash(linkTag)}`;
        return (
          <React.Fragment key={`headLinkDefault${key}${index}`}>
            {asValue === "font" ? (
              <link
                crossOrigin="anonymous"
                key={`link-${key}`}
                type="font/woff2"
                {...props}
              />
            ) : (
              <link key={`link-${key}`} {...props} />
            )}
          </React.Fragment>
        );
      } else if (
        linkTag &&
        typeof linkTag === "object" &&
        asValue === "image"
      ) {
        const props: LinkData = {
          [LinkAttr.href]: (linkTag.href || "").replace(
            ROOT_TOKEN,
            this.props.root
          ),
          [LinkAttr.rel]: rel,
          [LinkAttr.as]: asValue,
          [LinkAttr.media]: linkTag.media
        };
        const key = `${rel}${asValue || ``}-${removeProtocolOrSlash(
          linkTag.href || ""
        )}-${linkTag.media || ""}`;
        return (
          <React.Fragment key={`headLinkDefault${key}${index}`}>
            <link key={`link-${key}`} {...props} />
          </React.Fragment>
        );
      }
      return <></>;
    });
  };

  private getLinkTags = (link: LinkData[]): JSX.Element[] => {
    let linkArray: JSX.Element[] = [];
    const linkDefaults: LinkTags =
      this.props.default && this.props.default.link ?
        this.props.default.link :
        {};
    const dnsfetch: JSX.Element[] = this.getLinks(
      linkDefaults?.dnsFetch,
      `dns-prefetch`
    );
    const preloadFetch: JSX.Element[] = this.getLinks(
      linkDefaults?.preloadFetch,
      `preload`,
      `fetch`
    );
    const preloadScript: JSX.Element[] = this.getLinks(
      linkDefaults?.preloadScript,
      `preload`,
      `script`
    );
    const preloadStyle: JSX.Element[] = this.getLinks(
      linkDefaults?.preloadStyle,
      `preload`,
      `style`
    );
    const preloadImage: JSX.Element[] = this.getLinks(
      linkDefaults?.preloadImage,
      `preload`,
      `image`
    );
    const preloadFont: JSX.Element[] = this.getLinks(
      linkDefaults?.preloadFont,
      `preload`,
      `font`
    );
    const stylesheet: JSX.Element[] = this.getLinks(
      linkDefaults?.stylesheet,
      `stylesheet`
    );
    if (link && Array.isArray(link) && link.length > 0) {
      linkArray = link.map((linkData: LinkData, index: number): JSX.Element => {
        if (linkData && typeof linkData === "object") {
          const props: LinkData = {};
          for (const prop in LinkAttr) {
            if (linkData.hasOwnProperty(prop)) {
              props[prop] = linkData[prop];
            }
          }

          const resolvedHrefLang =
            props.hrefLang ||
            (linkData as any)?.hrefLang ||
            (linkData as any)?.hreflang ||
            props.hreflang;
          if (resolvedHrefLang) {
            props.hrefLang = resolvedHrefLang;
          }
          delete (props as any).hreflang;
          if (Object.keys(props).length > 0) {
            const key = `${props.rel || ``}${props.hrefLang || ``}${
              removeProtocolOrSlash(props.href) || ``
            }`;
            return (
              <React.Fragment key={`headLink${index}`}>
                <link key={`link-${key || index}`} {...props} />
              </React.Fragment>
            );
          }
        }
        return <></>;
      });
    }
    // Don't change the order of link Array as this can disturb the preload calls
    linkArray = preloadImage.concat(linkArray);
    linkArray = stylesheet.concat(linkArray);
    linkArray = preloadStyle.concat(linkArray);
    linkArray = preloadFont.concat(linkArray);
    linkArray = preloadScript.concat(linkArray);
    linkArray = preloadFetch.concat(linkArray);
    linkArray = dnsfetch.concat(linkArray);
    return linkArray;
  };

  private getMetaTags = (meta: MetaData[]): JSX.Element[] => {
    const metaDefaults: MetaData[] =
      this.props.default && this.props.default.meta ?
        this.props.default.meta :
        [];
    const metaHomepageDefaults: MetaData[] =
      this.props.default && this.props.default.metaHomepage ?
        this.props.default.metaHomepage :
        [];
    const metaTags: MetaData[] = meta && Array.isArray(meta) ? meta : [];
    let allMetaTags: MetaData[] = metaDefaults.concat(metaTags);

    if (this.props.page === "homepage" || this.props.page === "sb/homepage") {
      allMetaTags = metaDefaults.concat(metaHomepageDefaults, metaTags);
    }
    return allMetaTags.map((metaData: MetaData, index: number): JSX.Element => {
      if (metaData && typeof metaData === "object") {
        const props: MetaData = {};
        for (const prop in MetaAttr) {
          if (metaData.hasOwnProperty(prop)) {
            props[prop] = metaData[prop];
          }
        }
        if (Object.keys(props).length + 1) {
          const key = `${
            props.name ?
              props.name :
              props.property ?
              props.property :
              props.httpEquiv ?
              props.httpEquiv :
              props.charSet ?
              props.charSet :
              index
          }`;
          return (
            <React.Fragment key={`headLink${index}`}>
              <meta {...props} key={`meta-${key}`}/>
            </React.Fragment>
          );
        }
      }
      return <></>;
    });
  };

  /**
   * Processes the default script data.
   * This script data has a different structure than the data returned from the CMS.
   */
  private getScripts = (scripts: ScriptTags[]): JSX.Element[] => {
    const scriptTags: ScriptTags[] =
      scripts && Array.isArray(scripts) ? scripts : [];
    return scriptTags.map(
      (scriptTag: ScriptTags, index: number): JSX.Element => {
        if (scriptTag && typeof scriptTag === "object") {
          for (const script in scriptTag) {
            if (scriptTag.hasOwnProperty(script)) {
              const currentScript: ScriptData = scriptTag[script];
              const props: ScriptData = {
                [ScriptAttr.src]: script.replace(ROOT_TOKEN, this.props.root)
              };
              const customAttributes: SimpleJson =
                currentScript.custom as SimpleJson;
              if (
                script === "inline" &&
                currentScript.type &&
                currentScript.value
              ) {
                return (
                  <React.Fragment key={`headScriptDefault${index}`}>
                    <script
                      dangerouslySetInnerHTML={{ __html: currentScript.value }}
                      key={`script-${currentScript.type}${index}`}
                      type={currentScript.type}
                    />
                  </React.Fragment>
                );
              }
              if (currentScript.async) {
                props[ScriptAttr.async] = currentScript.async;
              }
              if (currentScript.defer) {
                props[ScriptAttr.defer] = currentScript.defer;
              }
              if (customAttributes) {
                for (const custAttr in customAttributes) {
                  if (customAttributes.hasOwnProperty(custAttr)) {
                    props[custAttr] = customAttributes[custAttr].replace(
                      ROOT_TOKEN,
                      this.props.root
                    );
                  }
                }
              }
              return (
                <React.Fragment key={`headScriptDefault${index}`}>
                  <script
                    key={`script-${
                      removeProtocolOrSlash(props[ScriptAttr.src]) || index
                    }`}
                    {...props}
                  />
                </React.Fragment>
              );
            }
          }
        }
        return <></>;
      }
    );
  };

  /**
   * Processes dynamic script data.
   * This script data has a different structure than the default data.
   */
  private getScriptTags = (
    script: ScriptData[],
    globalScript?: ScriptData[]
  ): JSX.Element[] => {
    let scriptArray: JSX.Element[] = [];
    let seoTags: JSX.Element[] = [];
    const scriptDeafults: ScriptTags[] =
      this.props.default && this.props.default.script ?
        this.props.default.script :
        [];
    const defaultTags: JSX.Element[] = this.getScripts(scriptDeafults);

    // eslint-disable-next-line no-param-reassign
    script =
      script && Array.isArray(script) && script.length > 0 ?
        script.concat(globalScript) :
        globalScript;
    if (script && Array.isArray(script) && script.length > 0) {
      scriptArray = script.map((scriptData: ScriptData, index: number) => {
        if (scriptData?.type && scriptData?.value) {
          if (scriptData.type === "application/ld+json") {
            return <></>;
          }
          return (
            <React.Fragment key={`headScript${index}`}>
              <script
                dangerouslySetInnerHTML={{ __html: scriptData.value }}
                key={`script-${scriptData.type}${index}`}
                type={scriptData.type}
              />
            </React.Fragment>
          );
        } else if (scriptData?.src) {
          const props: ScriptData = {};
          for (const prop in ScriptAttr) {
            if (scriptData.hasOwnProperty(prop)) {
              props[prop] = scriptData[prop];
            }
          }
          if (Object.keys(props).length + 1) {
            const key = `${removeProtocolOrSlash(props.src) || ``}`;
            return (
              <React.Fragment key={`headScript${index}`}>
                <script key={`script-${key || index}`} {...props} />
              </React.Fragment>
            );
          }
        }
        return <></>;
      });
      seoTags = script.map((scriptData: ScriptData, index: number) => {
        if (
          scriptData &&
          scriptData.type &&
          scriptData.type === "application/ld+json" &&
          scriptData.value
        ) {
          return (
            <React.Fragment key={`headSeoScript${index}`}>
              <script
                dangerouslySetInnerHTML={{ __html: scriptData.value }}
                key={`script-${scriptData.type}${index}`}
                type={scriptData.type}
              />
            </React.Fragment>
          );
        }
        return <></>;
      });
    }
    return seoTags.concat(defaultTags.concat(scriptArray));
  };

  private getStyles = (
    styles?: string[],
    globalStyles?: string[]
  ): JSX.Element => {
    const initialStyles: string[] =
      this.props.default && this.props.default.style ?
        this.props.default.style :
        [];
    const styleTags: string[] =
      styles && Array.isArray(styles) ?
        globalStyles.concat(styles) :
        [].concat(globalStyles);
    const allStyleTags: string[] = initialStyles.concat(styleTags);
    let content = ``;
    for (const style of allStyleTags) {
      if (style) {
        content += style;
      }
    }
    return (
      content && (
        <React.Fragment key={`headStyle`}>
          <style>{content}</style>
        </React.Fragment>
      )
    );
  };

  // eslint-disable-next-line class-methods-use-this
  private getTitleTag = (title: string): JSX.Element => (
    title && (
        <React.Fragment>
          <title key={`title-${title}`}>{title}</title>
        </React.Fragment>
    )
  );
}
